import { hasMarketingConsent, onMarketingConsentGranted } from './consent';
import { marketingData } from './state';
import { getCookie, setCookie } from './utils';

const UTM_COOKIE = 'vlb_utms';
const UTM_TTL_DAYS = 90;
const IMPACT_COOKIE = 'impact_im_ref';
const IMPACT_TTL_DAYS = 30;

type CampaignField =
  | 'source'
  | 'medium'
  | 'campaign'
  | 'content'
  | 'term'
  | 'gclid'
  | 'dclid'
  | 'adgroup'
  | 'campaign_id'
  | 'adgroup_id'
  | 'keyword_id'
  | 'match_type'
  | 'network'
  | 'device_from_ad';

// Mapowanie: nazwa parametru w URL → klucz w marketingData.
// utm_* to standard, reszta to Google Ads ValueTrack.
const UTM_PARAM_MAP: ReadonlyArray<readonly [string, CampaignField]> = [
  ['utm_source', 'source'],
  ['utm_medium', 'medium'],
  ['utm_campaign', 'campaign'],
  ['utm_content', 'content'],
  ['utm_term', 'term'],
  ['gclid', 'gclid'],
  ['dclid', 'dclid'],
  ['utm_adgroup', 'adgroup'],
  ['campaignid', 'campaign_id'],
  ['adgroupid', 'adgroup_id'],
  ['keywordid', 'keyword_id'],
  ['matchtype', 'match_type'],
  ['network', 'network'],
  ['device', 'device_from_ad'],
];

/** Hosty, z których wolno czytać UTM-y z referrera (CrazyEgg + nasza domena). */
const TRUSTED_REFERRER_HOST_SUFFIXES = [
  'crazyegg.com',
  'ce-cdn.com',
] as const;

type StoredUtms = Partial<Record<CampaignField, string>>;

export function getSourceFromReferrer(referrerUrl: string): string {
  if (!referrerUrl) return 'direct';
  try {
    const hostname = new URL(referrerUrl).hostname;
    if (hostname.includes('google.')) return 'google';
    if (hostname.includes('bing.com')) return 'bing';
    if (hostname.includes('yahoo.com')) return 'yahoo';
    if (hostname.includes('facebook.com') || hostname.includes('t.co')) return 'social';
    return hostname.replace(/^www\./, '');
  } catch {
    return 'direct';
  }
}

/** Zwraca query params z dowolnego URL-a (pusty zestaw, gdy URL jest nieprawidłowy). */
function paramsFromUrl(url: string): URLSearchParams {
  if (!url) return new URLSearchParams();
  try {
    return new URL(url).searchParams;
  } catch {
    return new URLSearchParams();
  }
}

function hostMatchesSuffix(hostname: string, suffix: string): boolean {
  return hostname === suffix || hostname.endsWith(`.${suffix}`);
}

/**
 * Czy z tego referrera wolno wyciągać UTM-y.
 * - ta sama domena (nawigacja / iframe parent) — OK
 * - CrazyEgg CDN/redirect — OK (fix ucinający UTM z URL)
 * - obcy host z własnymi utm_* — NIE (nie bierzemy cudzej kampanii)
 */
function isTrustedUtmReferrer(referrerUrl: string): boolean {
  if (!referrerUrl) return false;
  try {
    const refHost = new URL(referrerUrl).hostname.replace(/^www\./, '');
    const pageHost = window.location.hostname.replace(/^www\./, '');
    if (refHost === pageHost) return true;
    return TRUSTED_REFERRER_HOST_SUFFIXES.some((suffix) => hostMatchesSuffix(refHost, suffix));
  } catch {
    return false;
  }
}

function readStoredUtms(): StoredUtms {
  const raw = getCookie(UTM_COOKIE);
  if (!raw) return {};
  try {
    return JSON.parse(decodeURIComponent(raw)) as StoredUtms;
  } catch {
    return {};
  }
}

function writeStoredUtms(data: StoredUtms): void {
  setCookie(UTM_COOKIE, encodeURIComponent(JSON.stringify(data)), UTM_TTL_DAYS);
}

/**
 * Czy dany getter niesie sygnał realnej kampanii. Dzięki temu wejście "direct"
 * nie zamraża atrybucji — first-touch łapiemy dopiero przy pierwszym wejściu z kampanią.
 */
function hasCampaignSignal(get: (key: string) => string): boolean {
  return Boolean(
    get('utm_source') ||
      get('utm_medium') ||
      get('utm_campaign') ||
      get('utm_content') ||
      get('utm_term') ||
      get('gclid') ||
      get('dclid'),
  );
}

/**
 * Zbiera dane marketingowe dla BIEŻĄCEJ wizyty: URL → zaufany referrer.
 *
 * - Referrer (tylko same-origin / CrazyEgg): redirect CrazyEgg ucina UTM-y z URL,
 *   ale zostają w document.referrer — stąd je odzyskujemy.
 * - Cookie first-touch NIE wypełnia pól utm_* bieżącego deala — organic/direct
 *   zostaje pusty. Historia first-touch idzie osobno w first_touch_*.
 */
function incomingParams(): (urlKey: string) => string {
  const urlParams = paramsFromUrl(window.location.href);
  const referrerParams = isTrustedUtmReferrer(marketingData.referrer)
    ? paramsFromUrl(marketingData.referrer)
    : new URLSearchParams();
  return (urlKey: string): string => urlParams.get(urlKey) || referrerParams.get(urlKey) || '';
}

/**
 * FIRST-TOUCH: zapisuje komplet tylko raz — przy pierwszym wejściu z kampanią.
 * Wołane na każdej stronie ze skryptem, także bez formularza (np. /services/, blog),
 * żeby wejście z reklamy na stronę bez formularza nie gubiło źródła.
 * Tylko po zgodzie na cookies marketingowe (baner WPConsent); bez zgody nic nie czyta i nie zapisuje.
 */
export function captureFirstTouch(): StoredUtms {
  if (!hasMarketingConsent()) return {};
  const stored = readStoredUtms();
  const incoming = incomingParams();
  if (Object.keys(stored).length > 0 || !hasCampaignSignal(incoming)) return stored;
  const toStore: StoredUtms = {};
  for (const [urlKey, dataKey] of UTM_PARAM_MAP) {
    const val = incoming(urlKey);
    if (val) toStore[dataKey] = val;
  }
  writeStoredUtms(toStore);
  return toStore;
}

export function collectMarketingData(): void {
  const urlParams = paramsFromUrl(window.location.href);

  // Wartość przychodząca dla parametru: bieżący URL → zaufany referrer (bez cookie).
  const incoming = incomingParams();

  const firstTouch = captureFirstTouch();

  // Bieżąca atrybucja deala — TYLKO ta wizyta (URL / zaufany referrer). Bez sticky cookie.
  for (const [urlKey, dataKey] of UTM_PARAM_MAP) {
    marketingData[dataKey] = incoming(urlKey) || '';
  }

  // source: hostname referrera (organic google/bing) albo 'direct' — bez wymyślania kampanii.
  if (!marketingData.source) {
    marketingData.source = getSourceFromReferrer(marketingData.referrer) || 'direct';
  }

  // First-touch (cookie) — osobne pola, nie mylą się z bieżącym organic/direct.
  marketingData.first_touch_source = firstTouch.source || '';
  marketingData.first_touch_medium = firstTouch.medium || '';
  marketingData.first_touch_campaign = firstTouch.campaign || '';
  marketingData.first_touch_content = firstTouch.content || '';
  marketingData.first_touch_term = firstTouch.term || '';
  marketingData.first_touch_gclid = firstTouch.gclid || '';

  // Impact — własne cookie, niezależne od atrybucji UTM. Bez zgody tylko wartość z URL tej wizyty.
  let impactRef = urlParams.get('im_ref');
  const consent = hasMarketingConsent();
  if (impactRef) {
    if (consent) setCookie(IMPACT_COOKIE, impactRef, IMPACT_TTL_DAYS);
  } else {
    impactRef = consent ? getCookie(IMPACT_COOKIE) || '' : '';
  }
  marketingData.im_ref = impactRef;
}

/**
 * Zgoda kliknięta na stronie wejścia (bez przeładowania): UTM i im_ref są jeszcze w URL,
 * więc zapisujemy first-touch od razu i odświeżamy dane formularza.
 */
export function initMarketingConsentListener(): void {
  onMarketingConsentGranted(() => {
    collectMarketingData();
  });
}
